import logger from '@/utils/logger'

import isSerializedRichTextProbably from '@/client/services/richText/isSerializedRichTextProbably'

import type { FormContent, FormInputType, FormValues, Option } from '@/components/UI/Form/types'

import getPlainText from '../richText/getPlainText'

export function getLabel({
  label,
  fallback,
  convertRichTextToPlainText = false,
}: {
  label: FormContent | Option
  fallback: string
  convertRichTextToPlainText?: boolean
}): string {
  if (typeof label === 'string') {
    return label
  }

  if (isSerializedRichTextProbably(label) && convertRichTextToPlainText) {
    return getPlainText(label)
  }

  return fallback
}

function findOptionLabel<T>({
  options,
  value,
  convertRichTextToPlainText,
}: {
  options: Option[]
  value: T
  convertRichTextToPlainText?: boolean
}): string | T {
  const foundOption = options.find(option => option.value === value)
  return foundOption == null
    ? value
    : getLabel({
        label: foundOption.label,
        fallback: foundOption.value,
        convertRichTextToPlainText,
      })
}

// This helper function takes form values, a mapping from name -> value, and
// uses the form input list to create a mapping from label -> label. When an
// input is generated by form builder, the name / value can be a random hash, so
// this helps make objects more human readable.
export default function getLabelsFromValues({
  inputs,
  values,
  convertRichTextToPlainText = false,
}: {
  inputs: FormInputType[]
  values: FormValues
  convertRichTextToPlainText?: boolean
}): FormValues {
  const formattedValues: FormValues = {}

  inputs.forEach(item => {
    const label = getLabel({
      label: item.label,
      fallback: item.name,
      convertRichTextToPlainText,
    })

    const value = values[item.name]

    if (value == null) {
      formattedValues[label] = value
    } else if (item.type === 'multi-checkbox' || item.type === 'multi-select') {
      if (Array.isArray(value)) {
        const valueAsArray = value as string[]
        const newValue: string[] = []
        valueAsArray.forEach(valueItem => {
          const foundLabel = findOptionLabel({
            options: item.options,
            value: valueItem,
            convertRichTextToPlainText,
          })
          newValue.push(foundLabel)
        })
        formattedValues[label] = newValue
      } else {
        // No - op
        // Invalid user input
      }
    } else if (item.type === 'select' || item.type === 'select-tab') {
      const foundLabel = findOptionLabel({
        options: item.options,
        value,
        convertRichTextToPlainText,
      })
      formattedValues[label] = foundLabel
    } else if (item.type === 'select-group') {
      const foundLabel = findOptionLabel({
        options: item.options.flatMap(x => x.options),
        value,
        convertRichTextToPlainText,
      })
      formattedValues[label] = foundLabel
    } else if (item.type === 'radio') {
      const foundLabel = findOptionLabel({
        options: item.options,
        value,
        convertRichTextToPlainText,
      })
      formattedValues[label] = foundLabel
    } else if (item.type === 'text' || item.type === 'textarea') {
      formattedValues[label] = typeof value === 'string' ? value : null
    } else if (item.type === 'date') {
      formattedValues[label] = value
    } else if (item.type === 'number') {
      formattedValues[label] = value
    } else {
      logger.error('Unknown input type in getLabelsFromValues()', {
        item,
        inputs,
      })
    }
  })

  return formattedValues
}
